/**
 * Supports Create React App (`REACT_APP_*`) and Next.js (`NEXT_PUBLIC_*`) in the same shared bundle.
 *
 * Every variable is spelled out literally. Both bundlers inline only `process.env.<LITERAL>`
 * references at build time; a computed key such as process.env[`NEXT_PUBLIC_${name}`] is left
 * as-is and is always undefined in the browser, which silently disabled PostHog on the landing
 * page. The `name` union stops a new dynamic lookup from being added by accident.
 */
export type CrossFrameworkEnvName = 'POSTHOG_KEY' | 'POSTHOG_HOST' | 'POSTHOG_ENABLED' | 'BACKEND_URL';

export function readCrossFrameworkEnv(name: CrossFrameworkEnvName): string | undefined {
	try {
		switch (name) {
			case 'POSTHOG_KEY':
				return process.env.NEXT_PUBLIC_POSTHOG_KEY ?? process.env.REACT_APP_POSTHOG_KEY;
			case 'POSTHOG_HOST':
				return process.env.NEXT_PUBLIC_POSTHOG_HOST ?? process.env.REACT_APP_POSTHOG_HOST;
			case 'POSTHOG_ENABLED':
				// The one off switch for every browser event ('false' disables; default on).
				return process.env.NEXT_PUBLIC_POSTHOG_ENABLED ?? process.env.REACT_APP_POSTHOG_ENABLED;
			case 'BACKEND_URL':
				// The backend this build talks to (same pair HttpWrapper uses): names the environment below.
				return process.env.NEXT_PUBLIC_BACKEND_API_URL ?? process.env.REACT_APP_BASE_URL;
			default:
				return undefined;
		}
	} catch {
		// `process` itself may be absent in a browser bundle once the literals above are inlined.
		return undefined;
	}
}

/**
 * `environment` on every browser event (AnalyticsValues.environment): production | qa | lab | local.
 * Decided from the backend URL the build was made for, never from the page host: a customer's custom
 * domain may contain "qa" or "lab". A page served from localhost is a developer machine whatever
 * backend it points at.
 */
export function analyticsEnvironment(): string {
	try {
		const host = typeof window !== 'undefined' ? String(window.location?.hostname ?? '').toLowerCase() : '';
		if (host === 'localhost' || host === '127.0.0.1' || host.endsWith('.local')) return 'local';
	} catch {
		// no window; fall through to the build's backend URL
	}
	const backend = String(readCrossFrameworkEnv('BACKEND_URL') ?? '').toLowerCase();
	if (!backend || backend.includes('localhost') || backend.includes('127.0.0.1')) return 'local';
	if (backend.includes('lab') || backend.includes('devbackend')) return 'lab';
	if (backend.includes('qa')) return 'qa';
	return 'production';
}

/** False only when the public env says POSTHOG_ENABLED=false: the single browser off switch. */
export function isAnalyticsEnabled(): boolean {
	return String(readCrossFrameworkEnv('POSTHOG_ENABLED') ?? 'true').trim().toLowerCase() !== 'false';
}
